import { useMemo, useState } from "react";
import OrderSerialsTable, { ActionHandler } from "./OrderSerialsTable";
import EditOrderSerialDialog from "../../../dialogs/EditOrderSerialDialog";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Product } from "models/Product";
import EditSerialEntityDialog from "../../../dialogs/EditSerialEntityDialog";
import { useOrderContext } from "context/OrderContext";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  orderSerialToProduct,
  productToSerialEntity,
  serialEntityToProduct,
} from "utilities/serialEntityToOrderSerial";
import { OrderSerial } from "models/OrderSerial";
import CreateChildSerialDialog from "dialogs/CreateChildSerialDialog";
import DeleteProductDialog from "dialogs/DeleteProductDialog";
import EmptyMessage from "components/EmptyMessage";

const OrderProductsSection: React.FC = () => {
  const order = useOrderContext();

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [hideInstalled, setHideInstalled] = useState(false);
  const [searchString, setSearchString] = useState<string>("");

  const [showAddChildSerial, setShowAddChildSerial] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showDeleteProduct, setShowDeleteProduct] = useState(false);

  // Combine OrderSerials and SerialEntities into one list of Products
  const allProducts = useMemo(() => {
    const serials =
      order.productRows?.flatMap((row) => row.serials.map((os) => orderSerialToProduct(os))) ?? [];
    const serialEntities =
      order.checklists?.flatMap((checklist) =>
        checklist.serialEntities.map((se) => serialEntityToProduct(se, checklist))
      ) ?? [];
    return [...serials, ...serialEntities];
  }, [order]);

  const numberOfInstalled = useMemo(() => {
    return allProducts.filter((serial) => serial.installed).length;
  }, [allProducts]);

  const filteredSerials = useMemo(() => {
    return (
      allProducts
        .filter((serial) => !hideInstalled || !serial.installed)
        .filter(
          (serial) =>
            serial.serial?.toLowerCase().includes(searchString.toLowerCase()) ||
            serial.item?.toLowerCase().includes(searchString.toLowerCase()) ||
            serial.room?.toLowerCase().includes(searchString.toLowerCase()) ||
            serial.description?.toLowerCase().includes(searchString.toLowerCase())
        ) ?? []
    );
  }, [allProducts, searchString, hideInstalled]);

  const handleAction: ActionHandler = (product, action) => {
    setSelectedProduct(product);
    switch (action) {
      case "edit":
        setShowEditProduct(true);
        break;
      case "add-child":
        setShowAddChildSerial(true);
        break;
      case "delete":
        setShowDeleteProduct(true);
        break;
      default:
        console.warn(`Unknown ProductsTable action: ${action}`);
        break;
    }
  };

  return (
    <Box p={1}>
      {allProducts.length > 0 && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            label="Sök produkt"
            variant="filled"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            sx={(theme) => ({ width: "100%", [theme.breakpoints.up("sm")]: { maxWidth: "30ch" } })}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" hidden={!searchString}>
                    <IconButton onClick={(e) => setSearchString("")} edge="end">
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox checked={hideInstalled} onChange={() => setHideInstalled(!hideInstalled)} />
            }
            label="Dölj installerade"
            labelPlacement="end"
          />

          <Typography sx={{ ml: "auto" }}>
            <Box component="span" sx={{ fontWeight: "bold", fontSize: "large" }}>
              {`${numberOfInstalled} / ${allProducts.length}`}
            </Box>{" "}
            installerade
          </Typography>
        </Box>
      )}

      {allProducts.length === 0 ? (
        <EmptyMessage m={1}>Inga produkter registrerade</EmptyMessage>
      ) : (
        <OrderSerialsTable serials={filteredSerials} onAction={handleAction} />
      )}

      {showAddChildSerial && selectedProduct && (
        <CreateChildSerialDialog
          parent={selectedProduct}
          onClose={() => setShowAddChildSerial(false)}
        />
      )}

      {showEditProduct && selectedProduct && !selectedProduct.existing && (
        <EditOrderSerialDialog
          serial={selectedProduct as OrderSerial}
          onClose={() => {
            setSelectedProduct(null);
            setShowEditProduct(false);
          }}
        />
      )}

      {showEditProduct && selectedProduct && selectedProduct.existing && (
        <EditSerialEntityDialog
          serial={productToSerialEntity(selectedProduct)}
          onClose={() => {
            setSelectedProduct(null);
            setShowEditProduct(false);
          }}
        />
      )}

      {showDeleteProduct && selectedProduct && selectedProduct.editable && (
        <DeleteProductDialog
          product={selectedProduct}
          orderId={order.ordernr}
          onClose={() => {
            setSelectedProduct(null);
            setShowDeleteProduct(false);
          }}
        />
      )}
    </Box>
  );
};

export default OrderProductsSection;
